import Color from 'color';
import { debounce } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Tooltip, POSITIONS } from '@hivebrite/design-system/Tooltip';

import { getColorPresetsArray } from 'hb-builder/utils/colors';
import ColorPicker from 'hb-react/backoffice/components/DesignSystem/ColorPicker';
import { useQuillEditorContext } from 'hb-react/shared/components/DesignSystem/QuillEditor/contexts/QuillEditorContext';
import ToolbarItem from 'hb-react/shared/components/DesignSystem/QuillEditor/Toolbar/components/ToolbarItem';
import { addToStorage, getItem } from 'hb-react/shared/utils/sessionStorage';

const COLOR_PRESETS_LIMIT = 3;
const DEBOUNCE_CHANGE_COLOR = 150;
const SESSION_STORAGE_KEY = 'hivebrite-new-builder-colors';

const initialColorPresets = getItem({ key: SESSION_STORAGE_KEY });

const ColorTool = ({ brandColors, onItemClick, value: initialValue }) => {
  const theme = useTheme();

  const [isOpen, setOpen] = useState(false);

  const [colorPresets, setColorPresets] = useState(
    getColorPresetsArray({ colors: initialColorPresets, theme, length: 3 }),
  );
  const { quillRef } = useQuillEditorContext();

  const value = Array.isArray(initialValue) ? initialValue[0] : initialValue;

  const listColors = useMemo(
    () =>
      [
        brandColors && {
          name: 'brandColors',
          colors: brandColors.map(({ label, value }) => ({
            label,
            value,
            withTooltip: true,
          })),
        },
        colorPresets && {
          name: 'presets',
          colors: colorPresets
            .map((color, index) => ({
              label: `label-${index}`,
              value: Color(color).hex().toUpperCase(),
            }))
            .filter(Boolean),
        },
      ].filter(Boolean),
    [brandColors, colorPresets],
  );

  const handleChangeColor = useCallback(
    debounce((color) => {
      addToStorage({
        allowDuplicate: false,
        key: SESSION_STORAGE_KEY,
        value: color,
        limit: COLOR_PRESETS_LIMIT,
      });

      const storedColors = getItem({ key: SESSION_STORAGE_KEY });

      setColorPresets(
        getColorPresetsArray({ colors: storedColors, theme, length: 3 }),
      );
      quillRef.current.focus();
      quillRef.current.format('color', color, 'user');
    }, DEBOUNCE_CHANGE_COLOR),
    [],
  );

  const handleClick = () => {
    setOpen((prevState) => !prevState);
    onItemClick();
  };

  return (
    <>
      <Tooltip
        initialPosition={POSITIONS.BOTTOM}
        childrenElement={__('Text color')}
      >
        <ToolbarItem onClick={handleClick} isActive>
          <CurrentColorWrapper>
            <CurrentColor value={value || theme.colors.asphalt} />
          </CurrentColorWrapper>
        </ToolbarItem>
      </Tooltip>
      {isOpen && (
        <ColorPickerWrapper>
          <ColorPicker
            defaultColor="black"
            id="color"
            onChange={handleChangeColor}
            presetColors={listColors}
            value={value || theme.colors.asphalt}
            width="100%"
          />
        </ColorPickerWrapper>
      )}
    </>
  );
};

ColorTool.getFormat = (format, quill) =>
  format?.color || (quill && window.getComputedStyle(quill.container).color);

export default ColorTool;

const ColorPickerWrapper = styled(Box)`
  margin-left: 0 !important;
  margin-top: ${({ theme }) => theme.space[2]}px;
  user-select: none;
`;

const CurrentColor = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) => !['value'].includes(prop),
  })
  .attrs(() => ({
    height: '100%',
    width: '100%',
  }))`
  border-radius: 2px;
  ${({ value }) => css`
    background-color: ${value};
  `}
  `;

const CurrentColorWrapper = styled(Box).attrs(() => ({
  bg: 'white',
  height: '100%',
  p: '2px',
  width: '100%',
}))`
  border-radius: 2px;
  position: relative;
`;
